import React, { type FC, useState, useEffect, useContext } from 'react';

import { getI18nData, isBrowser } from '@lucid/utils';
import reactHTMLParser from 'html-react-parser';

import Box from '../box/box';
import Link from '../link/link';
import Text from '../text/text';
import type { Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';

export interface ChatbotDisclaimerProps {
  i18nData?: Lucid.I18n.DataEdges;
}

const ChatbotDisclaimer: FC<ChatbotDisclaimerProps> = ({ i18nData, ...rest }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);

  useEffect(() => {
    let iterations = 0;

    const checkForChatbot = (interval: NodeJS.Timeout) => {
      // We only need this disclaimer if the chatbot is present
      const chatbot = document?.querySelector('#q-messenger-frame');

      if (iterations > 2 && !chatbot) {
        setShouldRender(false);

        return clearInterval(interval);
      }
      // Chatbot iframe width is set to 20px when hidden
      const isChatbotActive =
        isBrowser() && chatbot
          ? parseInt(window.getComputedStyle(chatbot).width) > 20 &&
            window.getComputedStyle(chatbot).visibility !== 'hidden'
          : false;
      iterations++;

      // Longest chatbot delay is 30 seconds - 10x at 5s intervals gives us a small buffer
      // After that, we up the interval to 60 seconds to add/remove the disclaimer
      if (isChatbotActive || iterations > 10) {
        setShouldRender(isChatbotActive);
        const longInterval = setInterval(() => checkForChatbot(longInterval), 60000);

        return clearInterval(interval);
      }
      setShouldRender(isChatbotActive);

      return () => clearInterval(interval);
    };

    const baseInterval = setInterval(() => checkForChatbot(baseInterval), 5000);
  }, []);

  return shouldRender ? (
    <Box
      {...rest}
      css={theme => [
        {
          position: 'fixed',
          bottom: '58px',
          right: '-12px',
          padding: theme.space.s32,
          zIndex: theme.zIndices.z10,
          transitionProperty: 'clip-path, transform, right',
          transitionTimingFunction: 'ease-in-out',
          transitionDuration: '0.1s',
          backgroundColor: theme.colors.white,
          borderRadius: theme.radii.r16,
          boxShadow: theme.shadows.card,
          overflow: 'hidden',
          width: '356px',
          clipPath: 'circle(12px at 327px 21px)',
          transform: 'translateY(calc(100% - 16px))',
          border: `1px solid ${theme.colors.blue[70]}`,
        },
        isExpanded && {
          right: '24px',
          transform: 'translateY(0)',
          clipPath: 'circle(75%)',
          zIndex: theme.zIndices.zMax,
        },
      ]}
      data-testid="chatbot-disclaimer"
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsExpanded(false);
      }}
      onFocus={e => {
        if (e.target.tagName !== 'BUTTON') {
          setIsExpanded(true);
        }
      }}
      tabIndex={0}
    >
      <Box css={{ position: 'relative' }}>
        <Link
          asButton
          aria-label={i18n('mktg-chatbot-disclaimer-expand', { action: isExpanded ? 'close' : 'open' })}
          css={theme => [
            {
              position: 'absolute',
              top: `-${theme.space.s24}px`,
              right: `-${theme.space.s24}px`,
              fontSize: theme.fontSizes.fs24,
              marginRight: theme.space.s8,
              backgroundColor: theme.colors.white,
              zIndex: theme.zIndices.z10,
            },
            isExpanded && {
              backgroundColor: 'transparent',
            },
          ]}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          tabIndex={isExpanded ? 0 : -1}
          variant="basic"
        >
          <Box
            css={theme => [
              {
                border: `2px solid ${theme.colors.blue[70]}`,
                borderRadius: '50%',
                height: '24px',
                width: '24px',
                position: 'relative',
                '& div': {
                  position: 'absolute',
                  left: '9px',
                  transitionProperty: 'transform, top, left, max-height, min-height',
                  transitionTimingFunction: 'ease-in-out',
                  transitionDuration: '.4s',
                  transitionDelay: '.1s',
                  background: theme.colors.blue[70],
                  width: '2px',
                },
              },
              isExpanded && {
                '& div': {
                  top: '3px',
                  maxHeight: '14px',
                  minHeight: '14px',
                },
              },
            ]}
          >
            <Box
              css={[
                {
                  minHeight: '2px',
                  maxHeight: '2px',
                  top: '4px',
                  transform: 'rotate(0deg) scaleY(1)',
                },
                isExpanded && {
                  transform: 'rotate(45deg)',
                },
              ]}
            />
            <Box
              css={[
                {
                  minHeight: '8px',
                  maxHeight: '8px',
                  top: '8px',
                  transform: 'rotate(0deg) scaleY(1)',
                },
                isExpanded && {
                  transform: 'rotate(135deg)',
                },
              ]}
            />
          </Box>
        </Link>
        <Text
          css={theme => ({
            margin: `0 0 ${theme.space.s32}px 0`,
            position: 'relative',
            fontWeight: theme.fontWeights.fw500,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: `-${theme.space.s32}px`,
              left: `-${theme.space.s32}px`,
              right: 0,
              backgroundColor: theme.colors.blue[5],
              height: `calc(100% + ${theme.space.s48}px)`,
              width: `calc(100% + ${theme.space.s64}px)`,
              zIndex: theme.zIndices.zNegative1,
            },
          })}
        >
          {i18n('mktg-chatbot-disclaimer')}
        </Text>
        <Text css={theme => [theme.shared.paragraphNestedLinkStyles(theme, 'primaryAlternate'), { marginBottom: 0 }]}>
          {reactHTMLParser(
            i18n('mktg-chatbot-disclaimer-contact', {}, [
              `<a tabindex=${isExpanded ? 0 : -1} href="https://lucid.co/contact/contact-sales">{}</a>`,
            ]),
          )}
        </Text>
      </Box>
    </Box>
  ) : (
    <></>
  );
};
export { ChatbotDisclaimer };
export default ChatbotDisclaimer;
